import {
	BackdropCircularProgress,
	DownloadFileButton,
	EnhancedDropDown,
	EnhancedDropDownRHF,
	EnhancedSnackbar,
	IResponse,
	IServerError,
	ImportSlipsInstructions,
} from "@websential/cosmic-v20";
import { Box, Button, Card, Grid, Typography } from "@mui/material";
import {
	INITIAL_SNACKBAR_DATA,
	T4_LIST_SCREEN_SLUG,
} from "../../common/constants";
import React, { useEffect, useState } from "react";
import {
	setSelectedActivityLogActions,
	setSelectedStickyNavBar,
} from "../../store/actions";
import { useDispatch, useSelector } from "react-redux";
import { useMutation, useQuery } from "@tanstack/react-query";

import DownloadIcon from "@mui/icons-material/Download";
import { EXCEL_FILE_BASE64 } from "./constants";
import ForwardIcon from "@mui/icons-material/Forward";
import { IAppState } from "../../store/reducers";
import { YearToDateService } from "../../services/YearToDateService";
import { setSelectedCSVInformationUI } from "../../store/actions/CSVInformationUIAction";
import { setSelectedDrawerOptionsMenu } from "../../store/actions/drawerMenuOptionsActions";
import { useNavigate } from "react-router-dom";

// import SampleFile from "./Sample_Employee _T4Slips.xlxs"

const instructionList = [
	{ icon: ForwardIcon, text: "Download sample excel file." },
	{ icon: ForwardIcon, text: "Fill in employee(s) payroll data." },
	{ icon: ForwardIcon, text: "Save As CSV (Comma delimited) (*.csv)." },
	{ icon: ForwardIcon, text: "Employee Name and Gross are required fields." },
	{ icon: ForwardIcon, text: "Employee with existing Year to Date data will not be imported." },
	{ icon: ForwardIcon, text: "Import Year to Date data for employee(s) before processing any payroll in SimplePay" },
	{ icon: ForwardIcon, text: "CPP/QPP, E.I., QPIP max values for the year will be adjusted in payroll with year to date data" },
	{
		icon: ForwardIcon,
		text: "Upload the file for import and Press Retrive Data Button.",
	},
	{ icon: ForwardIcon, text: "Maximum allowed file size is one MB." },
];
const instructionTitle = "Instructions";
const instructionSubTitle = "File Instructions";
const YEARS_DDL_LIST_QUERY_KEY = "years-ddl";

const useGetYearsDDL = (clientId: string, taxYear: number) =>
	useQuery({
		queryKey: [YEARS_DDL_LIST_QUERY_KEY, clientId, taxYear],
		queryFn: (data) => {
			return YearToDateService.getYearsDDL(clientId, taxYear);
		},
		refetchOnWindowFocus: false,
	});

export const ImportYearToDate = () => {
	const [selectedFile, setSelectedFile] = useState(EXCEL_FILE_BASE64);
	const [snackbarData, setSnackbarData] = useState(INITIAL_SNACKBAR_DATA);
	const id = useSelector((state: IAppState) => state.client.id);
	const clientName = useSelector((state: IAppState) => state.client.clientName);
	const taxYear = useSelector((state: IAppState) => state.taxYear.year);
	const dispatch = useDispatch();
	const navigate = useNavigate();

	const { isPending: isPendingPayFrequencyDDL, data: yearsDDLItems } =
		useGetYearsDDL(id, taxYear);

	const handleFileChange = (event: any) => {
		const file = event.target.files[0];
		setSelectedFile(file);
	};

	const handleSubmit = (event: any) => {
		event?.preventDefault();
		const formData = new FormData();
		formData.append("file", selectedFile);

		let payload = {
			id,
			taxYear,
			formData,
		};
		t4SlipRetrieveDataByFile(payload);
	};

	const { isPending: isSubmitting, mutate: t4SlipRetrieveDataByFile } = /// change api here later 
		useMutation({
			mutationFn: YearToDateService.getImportYearToDate, /// chnage api url later 
			onSuccess: (res: any | IServerError | IResponse) => {

				dispatch(setSelectedCSVInformationUI({ tag: "T4", data: res }));
				let path = `/compliance/csv-information`; /// change this url later according to year to date 
				navigate(path);
				setSnackbarData({
					open: true,
					message: res.message,
					severity: "success",
				});
				// }
			},
			onError: (err: any) => {
				setSnackbarData({
					open: true,
					message: err.message,
					severity: "error",
				});
			},
		});

	const onSnackbarClosed = (
		event: React.SyntheticEvent | Event,
		reason?: string
	) => {
		if (reason === "clickaway") {
			return;
		}
		setSnackbarData(INITIAL_SNACKBAR_DATA);
	};

	const drawerMenuOptions = useSelector(
		(state: IAppState) => state.drawerMenu.menu
	);

	const expandedLabels = ["Settings", "Year To Date"];
	const expandedT4ListOrListItem = "Import";
	const enhanceData = (data: any[], parentLabel?: string): any[] => {
		return data.map((item) => {
			let isExpanded = expandedLabels.includes(item.label);
			if (parentLabel === "Year To Date" && item.label === expandedT4ListOrListItem) {
				isExpanded = true;
			}
			const enhancedItem = {
				...item,
				// expanded: isExpanded,
				// items: item.items ? enhanceData(item.items, item.label) : undefined,
				expanded: item.items ? isExpanded : false,
				items: item.items ? enhanceData(item.items, item.label) : undefined,
				path: item.label === expandedT4ListOrListItem && "/settings/year-to-date/import",
			};
			return enhancedItem;
		});
	};

	useEffect(() => {
		dispatch(
			setSelectedActivityLogActions({
				entityId1: "",
				entityId2: "",
				entityId3: "",
				screenName: T4_LIST_SCREEN_SLUG,
				companyName: clientName,
				activityType: "activity_type=import_csv",
				activityLogVisibility: true,
			})
		);
		dispatch(setSelectedStickyNavBar({ isSticky: false }));
		if (drawerMenuOptions && drawerMenuOptions.length > 0) {
			const enhancedData = enhanceData(drawerMenuOptions);
			dispatch(setSelectedDrawerOptionsMenu({ menu: enhancedData }));
		}
	}, [drawerMenuOptions]);

	return (
		<>
			<BackdropCircularProgress
				open={isSubmitting || isPendingPayFrequencyDDL}
				testId="import-resource-backdrop"
			/>
			<form onSubmit={handleSubmit}>
				<Box sx={{ width: "100%", marginTop: "2rem" }}>
					<Grid
						container
						rowSpacing={1}
						columnSpacing={{ xs: 1, sm: 2, md: 3 }}
					>
						<Grid size={{ xs: 12, sm: 6, md: 6, lg: 6, xl: 6 }}>
							<Card>
								<Box
									sx={{
										fontSize: ".9rem",
										color: "#000",
										padding: "20px 16px 16px 26px",
										fontWeight: "bold",
									}}
								>
									Import
								</Box>
								<Box sx={{ padding: "16px", height: "441px" }}>
									<Grid container>
										<Grid
											size={{ xs: 12, sm: 12, md: 5, lg: 5, xl: 5 }}
											sx={{ p: "12px" }}
										>
											<Typography
												sx={{
													color: "rgba(0,0,0,.6)",
													fontSize: ".875rem",
													p: "12px",
												}}
											>
												Year
											</Typography>
										</Grid>
										<Grid
											size={{ xs: 12, sm: 12, md: 7, lg: 7, xl: 7 }}
											sx={{ p: "12px" }}
										>
											<Box sx={{ p: "12px", width: "50%", "& .dropDownWrapper": { marginBottom: "0!important" } }}>
												<EnhancedDropDown
													selectName="yearDDLItems"
													testId={`yearDDLItems-list`}
													items={yearsDDLItems ?? []}
												// onSelectionChangeHandler={onYearSelectionChangeHandler}
												/>
											</Box>
										</Grid>
									</Grid>
									<Grid container>
										<Grid
											size={{ xs: 12, sm: 12, md: 5, lg: 5, xl: 5 }}
											sx={{ p: "12px" }}
										>
											<Typography
												sx={{
													color: "rgba(0,0,0,.6)",
													fontSize: ".875rem",
													p: "12px",
												}}
											>
												Import
											</Typography>
										</Grid>
										<Grid
											size={{ xs: 12, sm: 12, md: 7, lg: 7, xl: 7 }}
											sx={{ p: "12px" }}
										>
											<Box sx={{ p: "12px" }}>
												<input
													accept=".csv"
													type="file"
													onChange={handleFileChange}
													style={{ color: "rgba(0,0,0,.6)" }}
												/>
												<Typography
													variant="body2"
													sx={{ pt: "8px", color: "rgba(0,0,0,.6)" }}
												>
													Please upload .csv file.
												</Typography>
											</Box>
										</Grid>
									</Grid>
									<Grid container>
										<Grid
											size={{ xs: 12, sm: 12, md: 5, lg: 5, xl: 5 }}
											sx={{ p: "12px", mt: "20px" }}
										>
											<DownloadFileButton
												text={"Download Sample File"}
												icon={<DownloadIcon />}
												testId={"download-file-button"}
												fileName={"Sample_Employee_T4Slips.xlsx"}
												base64={EXCEL_FILE_BASE64}
											/>
											{/* <IconTextButton text={"Download Sample File"} icon={<DownloadIcon/>} navigateTo={"/Sample_Employee _T4Slips.xlxs"} testId={"download-file-button"}/> */}
										</Grid>
										<Grid
											size={{ xs: 12, sm: 12, md: 7, lg: 7, xl: 7 }}
											sx={{ p: "12px", mt: "20px", pl:"24px" }}
										>
											<Button variant="contained" type="submit">
												Retrieve data
											</Button>
										</Grid>
									</Grid>
								</Box>
							</Card>
						</Grid>
						<Grid size={{ xs: 12, sm: 12, md: 6, lg: 6, xl: 6 }}>
							<ImportSlipsInstructions
								instructions={instructionList}
								title={instructionTitle}
								subTitle={instructionSubTitle}
								testId="t4-slip-import-instructions"
							/>
						</Grid>
					</Grid>
				</Box>
				<EnhancedSnackbar
					message={snackbarData.message}
					onCloseHandler={onSnackbarClosed}
					severity={snackbarData.severity}
					testId={"resource-list-snackbar"}
					open={snackbarData.open}
				/>
			</form>
		</>
	);
};

export default ImportYearToDate;
